import { z } from "zod";
import {
  ConditionField,
  ConditionOperator,
  LogicalOperator,
  MemberType,
  RuleActionType,
  RuleLimitType,
  RuleRounding,
  RuleStatus,
} from "../enums";
import { validateEarningRule } from "../helpers/rule";
import type { IRuleConditionGroup } from "../models/rule";
import { optionalDateSchema } from "./common.schema";

const conditionScalarSchema = z.union([z.string(), z.number(), z.boolean()]);

const ruleConditionSchema = z.object({
  id: z.string(),
  field: z.enum(ConditionField),
  operator: z.enum(ConditionOperator),
  value: conditionScalarSchema.nullable().optional(),
  values: z.array(z.union([z.string(), z.number()])).optional(),
  range: z
    .object({
      min: z.union([z.number(), z.string()]),
      max: z.union([z.number(), z.string()]),
    })
    .optional(),
  valueLabels: z.record(z.string(), z.string()).optional(),
});

/** Recursive AND/OR condition group. */
export const ruleConditionGroupSchema: z.ZodType<IRuleConditionGroup> = z.lazy(() =>
  z.object({
    id: z.string(),
    operator: z.enum(LogicalOperator),
    conditions: z.array(ruleConditionSchema),
    groups: z.array(ruleConditionGroupSchema),
  }),
);

const ruleActionSchema = z.object({
  id: z.string(),
  type: z.enum(RuleActionType),
  amount: z.number({ message: "rule_action_amount_invalid" }),
  rounding: z.enum(RuleRounding).nullable().optional(),
  currency: z.string().optional(),
});

const ruleLimitSchema = z.object({
  id: z.string(),
  type: z.enum(RuleLimitType),
  value: z.number(),
});

const ruleExclusionsSchema = z.object({
  excludeRefunded: z.boolean(),
  excludeWhenStackedWith: z.array(z.string()),
  excludedSalesChannels: z.array(z.string()),
});

/** Field each {@link validateEarningRule} error code is reported against. */
const RULE_ERROR_PATHS: Record<string, string> = {
  rule_name_required: "name",
  rule_actions_required: "actions",
  rule_multiplier_invalid: "actions",
  rule_action_amount_invalid: "actions",
  rule_date_range_invalid: "endDate",
  rule_nesting_too_deep: "conditions",
  rule_empty_condition_group: "conditions",
  rule_condition_incomplete: "conditions",
  rule_priority_invalid: "priority",
  rule_limit_invalid: "limits",
};

/**
 * Validation schema for `EarningRuleInput`. Structural checks are done by zod;
 * business checks (actions, nesting, empty groups, incomplete conditions, date
 * window, limits) reuse {@link validateEarningRule} so the builder and the form
 * report the same translation-key codes.
 */
export const earningRuleSchema = z
  .object({
    programId: z.string().min(1, { message: "program_required" }),
    name: z.string().max(120, { message: "max_characters_error" }),
    description: z.string().max(1000, { message: "max_characters_error" }).optional(),
    status: z.enum(RuleStatus),
    memberType: z.enum(MemberType),
    priority: z.number().int({ message: "rule_priority_invalid" }),
    stackable: z.boolean(),
    startDate: optionalDateSchema,
    endDate: optionalDateSchema,
    conditions: ruleConditionGroupSchema,
    actions: z.array(ruleActionSchema),
    limits: z.array(ruleLimitSchema),
    exclusions: ruleExclusionsSchema,
  })
  .superRefine((data, context) => {
    for (const code of new Set(validateEarningRule(data))) {
      context.addIssue({
        code: "custom",
        message: code,
        path: [RULE_ERROR_PATHS[code] ?? "conditions"],
      });
    }
  });

export type EarningRuleFormValues = z.infer<typeof earningRuleSchema>;
